import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, api } from '../../api/apiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { fuzzyMatchProduct } from '../../utils/fuzzySearch';
import { Pagination } from '../../components/ui/Pagination';

// Helper to safely get color string
const getColorName = (c) => {
    if (!c) return null;
    return typeof c === 'object' ? c.color : c;
};

function EditProductModal({ product, isOpen, onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        description: '',
        material: '',
        style: '',
        colors: '',
        sizes: '',
        variant_group_id: ''
    });

    React.useEffect(() => {
        if (product) {
            setFormData({
                name: product.name || '',
                price: product.price || '',
                description: product.description || '',
                material: product.material || '',
                style: product.style || '',
                colors: product.colors ? product.colors.join(', ') : '',
                sizes: product.sizes ? product.sizes.join(', ') : '',
                variant_group_id: product.variant_group_id || ''
            });
        }
    }, [product]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Exclude site_name and other non-db fields if necessary
        const { site_name, ...productData } = product;
        onSave({
            ...productData,
            ...formData,
            price: parseFloat(formData.price),
            colors: formData.colors.split(',').map(c => c.trim()).filter(c => c),
            sizes: formData.sizes.split(',').map(s => s.trim()).filter(s => s),
            variant_group_id: formData.variant_group_id || null
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Modifier le produit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid gap-2">
                        <Label htmlFor="name">Nom</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="price">Prix</Label>
                        <Input
                            id="price"
                            type="number"
                            step="0.01"
                            value={formData.price}
                            onChange={e => setFormData({ ...formData, price: e.target.value })}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="description">Description</Label>
                        <textarea
                            id="description"
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="material">Matière</Label>
                            <Input
                                id="material"
                                value={formData.material}
                                onChange={e => setFormData({ ...formData, material: e.target.value })}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="style">Style</Label>
                            <Input
                                id="style"
                                value={formData.style}
                                onChange={e => setFormData({ ...formData, style: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="colors">Couleurs (séparées par des virgules)</Label>
                        <Input
                            id="colors"
                            value={formData.colors}
                            onChange={e => setFormData({ ...formData, colors: e.target.value })}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="sizes">Tailles (séparées par des virgules)</Label>
                        <Input
                            id="sizes"
                            value={formData.sizes}
                            onChange={e => setFormData({ ...formData, sizes: e.target.value })}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="variant_group_id">Groupe ID (Laisser vide pour dissocier)</Label>
                        <Input
                            id="variant_group_id"
                            value={formData.variant_group_id}
                            onChange={e => setFormData({ ...formData, variant_group_id: e.target.value })}
                            placeholder="ex: verified_..."
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
                        <Button type="submit">Enregistrer</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function BulkEditModal({ isOpen, onClose, onSave, count }) {
    const [mode, setMode] = useState('colors'); // 'colors' or 'style'
    const [colors, setColors] = useState('');
    const [style, setStyle] = useState('');

    const handleSave = () => {
        const data = {};
        if (mode === 'colors') {
            data.colors = colors.split(',').map(c => c.trim()).filter(c => c);
        } else if (mode === 'style') {
            data.style = style.trim();
        }
        onSave(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Modifier ({count} produits)</DialogTitle>
                </DialogHeader>

                <div className="flex gap-2 mb-4 border-b pb-2">
                    <button
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${mode === 'colors' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setMode('colors')}
                    >
                        Couleurs
                    </button>
                    <button
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${mode === 'style' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setMode('style')}
                    >
                        Style (Catégorie)
                    </button>
                </div>

                <div className="grid gap-4 py-4">
                    {mode === 'colors' ? (
                        <div className="grid gap-2">
                            <Label htmlFor="bulk-colors">Nouvelles couleurs</Label>
                            <Input
                                id="bulk-colors"
                                value={colors}
                                onChange={(e) => setColors(e.target.value)}
                                placeholder="ex: Noir, Rouge"
                            />
                            <p className="text-xs text-muted-foreground">
                                Attention: Écrase les couleurs existantes.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-2">
                            <Label htmlFor="bulk-style">Nouveau Style</Label>
                            <Input
                                id="bulk-style"
                                value={style}
                                onChange={(e) => setStyle(e.target.value)}
                                placeholder="ex: Pantalon, Robe..."
                            />
                            <p className="text-xs text-muted-foreground">
                                Attention: Écrase le style existant.
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSave}>Appliquer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ProductCard({ product, allProducts = [], onEdit, onDelete, onRestore, isDeletedView, isSelected, onToggleSelect }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [activeProduct, setActiveProduct] = useState(product);

    // Trouver les variantes (même group_id, excluant le produit actuel)
    // Exclure les groupes "single" qui ne sont pas de vraies variantes
    const variants = product.variant_group_id && !product.variant_group_id.includes('single')
        ? allProducts.filter(p =>
            p.variant_group_id === product.variant_group_id &&
            p.id !== product.id
        )
        : [];

    const images = activeProduct.images || [];
    const hasMultipleImages = images.length > 1;

    const nextImage = (e) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => prev + 1 >= images.length ? 0 : prev + 1);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => prev - 1 < 0 ? images.length - 1 : prev - 1);
    };

    return (
        <Card
            className={`overflow-hidden cursor-pointer transition-shadow ${isSelected ? 'ring-2 ring-amber-500 shadow-md' : 'hover:shadow-md'}`}
            onClick={() => onToggleSelect(product.id)}
        >
            <div className="relative flex">
                {/* Selection Checkbox Overlay */}
                <div className="absolute top-2 left-2 z-20">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onToggleSelect(product.id);
                        }}
                        className="h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 shadow-md"
                    />
                </div>

                {/* Image principale */}
                <div className="relative group flex-1">
                    {images.length > 0 && (
                        <>
                            <img
                                src={images[currentImageIndex]}
                                alt={activeProduct.name}
                                className="w-full h-96 max-h-96 object-cover transition-opacity duration-300"
                                style={{ height: '384px', maxHeight: '384px' }}
                                onError={(e) => e.target.style.display = 'none'}
                            />
                            {hasMultipleImages && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                                        {images.map((_, idx) => (
                                            <button
                                                key={idx}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentImageIndex(idx);
                                                }}
                                                className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex
                                                    ? 'bg-white w-4'
                                                    : 'bg-white/50 hover:bg-white/75'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Colonne Variantes (à droite) */}
                {variants.length > 0 && (
                    <div className="w-10 flex flex-col gap-1 p-1 bg-gray-50 border-l overflow-y-auto max-h-96" style={{ height: '384px' }}>
                        {/* Produit original (si on est sur une variante) */}
                        <div
                            className={`relative w-8 h-8 rounded overflow-hidden cursor-pointer border-2 transition-all flex-shrink-0 ${activeProduct.id === product.id ? 'border-amber-500 ring-1 ring-amber-500' : 'border-transparent hover:border-gray-300'}`}
                            onClick={() => {
                                setActiveProduct(product);
                                setCurrentImageIndex(0);
                            }}
                            title={getColorName(product.colors?.[0]) || "Original"}
                        >
                            <img
                                src={product.images?.[0]}
                                className="w-full h-full object-cover"
                                alt="Original"
                            />
                        </div>

                        {/* Autres variantes */}
                        {variants.map(variant => (
                            <div
                                key={variant.id}
                                className={`relative w-8 h-8 rounded overflow-hidden cursor-pointer border-2 transition-all flex-shrink-0 ${activeProduct.id === variant.id ? 'border-amber-500 ring-1 ring-amber-500' : 'border-transparent hover:border-gray-300'}`}
                                onClick={() => {
                                    setActiveProduct(variant);
                                    setCurrentImageIndex(0);
                                }}
                                title={getColorName(variant.colors?.[0]) || variant.name}
                            >
                                <img
                                    src={variant.images?.[0]}
                                    className="w-full h-full object-cover"
                                    alt={variant.name}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <CardContent className="p-4">
                <h3 className="font-semibold mb-2 line-clamp-2">
                    {product.name}
                </h3>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex flex-col">
                        {product.is_on_sale && product.original_price && (
                            <span className="text-sm text-gray-500 line-through">
                                {product.original_price} €
                            </span>
                        )}
                        <span className={`text-lg font-bold ${product.is_on_sale ? 'text-red-600' : ''}`}>
                            {product.price} €{product.currency !== 'EUR' ? ` (${product.currency})` : ''}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {product.images && (
                            <Badge variant="secondary">
                                {product.images.length} photos
                            </Badge>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => onEdit(product)} className="h-6 w-6 p-0">
                            ✏️
                        </Button>
                        {isDeletedView ? (
                            <Button variant="ghost" size="sm" onClick={() => onRestore(product.id)} className="h-6 w-6 p-0 text-green-500 hover:text-green-700" title="Restaurer">
                                ♻️
                            </Button>
                        ) : (
                            <Button variant="ghost" size="sm" onClick={() => onDelete(product.id)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700">
                                🗑️
                            </Button>
                        )}
                    </div>
                </div>

                {product.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {product.description}
                    </p>
                )}

                <div className="flex flex-wrap gap-1 mb-2">
                    {product.sizes?.slice(0, 5).map((size, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                            {size}
                        </Badge>
                    ))}
                    {product.sizes?.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                            +{product.sizes.length - 5}
                        </Badge>
                    )}
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                    {product.colors?.slice(0, 3).map((color, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                            {getColorName(color)}
                        </Badge>
                    ))}
                    {product.colors?.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                            +{product.colors.length - 3}
                        </Badge>
                    )}
                </div>

                {product.material && (
                    <div className="mb-2">
                        <span className="text-xs font-semibold text-gray-500">Matière: </span>
                        <span className="text-xs text-gray-700">{product.material}</span>
                    </div>
                )}

                {product.style && (
                    <div className="mb-3">
                        <span className="text-xs font-semibold text-gray-500">Style: </span>
                        <Badge variant="secondary" className="text-xs">{product.style}</Badge>
                    </div>
                )}

                <a
                    href={product.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-amber-600 hover:text-amber-700 hover:underline block"
                >
                    Voir le produit →
                </a>

                <p className="text-xs text-muted-foreground mt-2">
                    Scrappé: {new Date(product.last_scraped_at).toLocaleDateString('fr-FR')}
                </p>
            </CardContent>
        </Card >
    );
}

import { normalizeCategory } from '../../utils/categoryUtils';

export function ScrapedProductsPage() {
    const [selectedSite, setSelectedSite] = useState('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [selectedColor, setSelectedColor] = useState('all');
    const [selectedSize, setSelectedSize] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all'); // Changed from selectedStyle
    const [showVariantsOnly, setShowVariantsOnly] = useState(false);
    const [showNoColor, setShowNoColor] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);

    // Pagination State
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    const queryClient = useQueryClient();

    const { data: sitesData } = useQuery({
        queryKey: ['scraper-sites'],
        queryFn: async () => {
            const response = await api.get('/scraper-sites');
            return response.data;
        }
    });

    const { data: productsData, isLoading } = useQuery({
        queryKey: ['scraped-products', selectedSite, showDeleted, page, limit, selectedCategory, selectedColor, minPrice, maxPrice, debouncedSearch, selectedSize, showVariantsOnly, showNoColor],
        queryFn: async ({ queryKey }) => {
            const [_key, _site, _deleted, _page, _limit, _cat, _col, _min, _max, _search, _size, _hasVariants, _noColor] = queryKey;
            const offset = (page - 1) * limit;
            const params = new URLSearchParams({
                limit: limit.toString(),
                offset: offset.toString()
            });

            if (_site !== 'all') params.append('siteId', _site);
            if (_deleted) params.append('deleted_only', 'true');
            if (_cat !== 'all') params.append('subcategories', _cat);
            if (_col !== 'all') params.append('colors', _col);

            if (_min) params.append('minPrice', _min);
            if (_max) params.append('maxPrice', _max);
            if (_search) params.append('q', _search);
            if (_size !== 'all') params.append('sizes', _size);
            if (_hasVariants) params.append('has_variants', 'true');
            if (_noColor) params.append('no_color', 'true');

            let url = '/api/scraped-products';

            // Note: backend 'by-site' logic handles site_id param via query too if we use main route
            // But we keep existing structure if needed. 
            // Actually, backend main route handles site_id via ?site_id if we want? 
            // Checked backend: it handles `filters.site_id` from `req.query.site_id`? 
            // Route /api/scraped-products uses `req.query.site_id`? 
            // Let's check backend code...
            // Backend `scrapedProducts.ts` line 165: `if (filters.site_id) ...`
            // But where is it parsed? 
            // Not explicitly parsed in `scrapedProducts.ts` snippet I saw! 
            // It parses `brands`, `minPrice`, `maxPrice`. 
            // Wait, checking line 237 of ScrapedProduct.ts... `if (filters.site_id)`.
            // But route must parse it. 
            // Looking at `scrapedProducts.ts` again...
            // It calls `findBySite` if `/by-site/:id` is used.
            // Main `/` route: 
            // It does NOT seem to parse `site_id` from query?
            // `filters.brands` is parsed. 
            // So for `selectedSite`, we MUST use the `/by-site/` endpoint OR update backend.
            // Current code uses `/by-site/` if selectedSite !== 'all'.
            // BUT `/by-site/:id` endpoint (lines 356+) in backend:
            // `ScrapedProductModel.findBySite(siteId, limit)` 
            // It DOES NOT ACCEPTS FILTERS! (no `filters` arg passed).
            // THIS IS ANOTHER BUG. If I use Site Filter, I LOSE Color/Category filters!

            // FIX: We should use the main endpoint `/` for EVERYTHING if possible.
            // Does main endpoint support `site_id` filter?
            // checking backend `scrapedProducts.ts`:
            // `filters` object construction (lines 178+).
            // It does NOT parse `site_id`. 
            // It parses `brands`.

            // So: I CANNOT use main endpoint for Site ID unless I fix backend to parse `site_id`.
            // OR I assume `selectedSite` maps to a brand name? 
            // `sitesData` has `id, name`.
            // If I send `brands=Name`, it works.

            // PROPOSED FIX: Send `brands` param using the site Name found in `sitesData`.

            // Legacy block removed.

            // Let's rely on `brands` param for now if we can find the name. 
            // Or better: Use the main endpoint and simply use `brands` filter if we have it.
            // Ideally I should fix backend to accept `site_id`. 
            // But for now let's try to proceed with main endpoint.

            // RE-READING BACKEND:
            // `router.get('/', async (req, res) => {`
            // `filters.brands = parseArray(req.query.brands);`
            // It does not parse `site_id`.

            // Okay, I will modify the URL construction to use main endpoint.
            // And I will try to pass `brands` if I can. 
            // If `sitesData` is available.

            // WAIT, `sitesData` comes from another hook. 
            // accessing it inside `queryFn` is fine as it closes over scope.

            // Fallback: If `selectedSite` is active, and I can't filter by it properly with filters... 
            // The user said "Global filters work for brands". 
            // Current code: 
            // `url = ... /by-site/${selectedSite}...`
            // And that endpoint ignores other filters.
            // So if user selects Site + Color, Color is IGNORED.
            // User didn't complain about THAT yet. 
            // User complained about "Color filter only per page". 
            // That applies when `selectedSite === 'all'`.

            // So for `selectedSite === 'all'`, I use main endpoint. 
            // params are appended. 

            let finalUrl = `/api/scraped-products?${params.toString()}`;

            const response = await fetch(finalUrl);
            return response.json();
        },
        keepPreviousData: true,
        refetchInterval: 10000
    });

    // Reset page when filters change
    React.useEffect(() => {
        setPage(1);
    }, [selectedSite, showDeleted, debouncedSearch, minPrice, maxPrice, selectedColor, selectedSize, selectedCategory, showVariantsOnly, showNoColor]);

    // Fetch dynamic category rules
    const { data: rules = [] } = useQuery({
        queryKey: ['categoryRules'],
        queryFn: () => apiClient.admin.getRules().catch(() => []),
        staleTime: 1000 * 60 * 5,
    });

    const matchCategory = React.useCallback((rawCat) => {
        if (!rawCat) return null;
        const lower = rawCat.trim().toLowerCase();

        for (const rule of rules) {
            for (const keyword of rule.keywords) {
                if (lower.includes(keyword.toLowerCase())) return rule.name;
            }
        }
        return rawCat.charAt(0).toUpperCase() + rawCat.slice(1).toLowerCase();
    }, [rules]);

    const allProducts = productsData?.products || [];

    // Extract unique values for filters
    const uniqueColors = React.useMemo(() => {
        if (productsData?.filterOptions?.colors?.length > 0) {
            return productsData.filterOptions.colors.sort();
        }
        const colors = new Set();
        (allProducts || []).forEach(p => {
            if (Array.isArray(p.colors)) p.colors.forEach(c => colors.add(getColorName(c)));
        });
        return Array.from(colors).sort();
    }, [productsData, allProducts]);

    const uniqueSizes = React.useMemo(() => {
        if (productsData?.filterOptions?.sizes?.length > 0) {
            return productsData.filterOptions.sizes.sort();
        }
        const sizes = new Set();
        (allProducts || []).forEach(p => {
            if (Array.isArray(p.sizes)) p.sizes.forEach(s => sizes.add(s));
        });
        return Array.from(sizes).sort();
    }, [productsData, allProducts]);

    const uniqueCategories = React.useMemo(() => {
        if (productsData?.filterOptions?.styles?.length > 0) {
            const cats = new Set();
            productsData.filterOptions.styles.forEach(s => {
                const cat = matchCategory(s);
                if (cat) cats.add(cat);
            });
            return Array.from(cats).sort();
        }
        const cats = new Set();
        (allProducts || []).forEach(p => {
            const cat = matchCategory(p.subcategory || p.style);
            if (cat) cats.add(cat);
        });
        return Array.from(cats).sort();
    }, [productsData, allProducts, matchCategory]);

    const filteredProducts = [...(allProducts || [])].sort((a, b) => {
        if (showVariantsOnly && a.variant_group_id && b.variant_group_id) {
            return a.variant_group_id.localeCompare(b.variant_group_id);
        }
        return 0;
    });

    // ... mutations ... (unchanged)

    const { data: enrichmentStatus, refetch: refetchEnrichment } = useQuery({
        queryKey: ['enrichment-status'],
        queryFn: async () => {
            const res = await api.get('/scraper/enrich-status');
            return res.data;
        },
        refetchInterval: (data) => data?.isRunning ? 2000 : false
    });

    const enrichMutation = useMutation({
        mutationFn: async () => {
            await api.post('/scraper/enrich-all');
            refetchEnrichment();
        }
    });

    const stopEnrichmentMutation = useMutation({
        mutationFn: async () => {
            await api.post('/scraper/enrich-stop');
            refetchEnrichment();
        }
    });

    const verifyGroupsMutation = useMutation({
        mutationFn: async () => {
            const res = await api.post('/scraper/verify-groups');
            return res.data;
        },
        onSuccess: (data) => {
            alert('Vérification bot démarrée ! Consultez la console du serveur pour voir la progression.');
            // Refetch after 10 seconds to allow bot to complete
            setTimeout(() => {
                refetchEnrichment();
                window.location.reload(); // Force reload to see updated data
            }, 10000);
        },
        onError: (error) => {
            alert("Erreur lors de la vérification des groupes");
            // console.error(error);
        }
    });

    // Description Cleaning
    const { data: cleaningStatus, refetch: refetchCleaning } = useQuery({
        queryKey: ['cleaning-status'],
        queryFn: async () => {
            const res = await api.get('/scraper/clean-status');
            return res.data;
        },
        refetchInterval: (data) => data?.isRunning ? 2000 : false
    });

    const startCleaningMutation = useMutation({
        mutationFn: async () => {
            await api.post('/scraper/clean-descriptions');
            refetchCleaning();
        }
    });

    const stopCleaningMutation = useMutation({
        mutationFn: async () => {
            await api.post('/scraper/clean-stop');
            refetchCleaning();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const res = await api.delete(`/scraped-products/${id}`);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['scraped-products']);
        }
    });

    const restoreMutation = useMutation({
        mutationFn: async (id) => {
            const res = await api.post(`/scraped-products/${id}/restore`);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['scraped-products']);
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (product) => {
            const res = await api.put(`/scraped-products/${product.id}`, product);
            return res.data;
        },
        onSuccess: () => {
            setEditingProduct(null);
            queryClient.invalidateQueries(['scraped-products']);
        },
        onError: (error) => {
            // console.error('Update failed:', error);
            alert('Erreur lors de la mise à jour du produit: ' + (error.response?.data?.error || error.message));
        }
    });

    const removeFromGroupMutation = useMutation({
        mutationFn: async (id) => {
            const res = await api.put(`/scraped-products/${id}`, { variant_group_id: null });
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['scraped-products']);
        }
    });

    const handleRemoveFromGroup = (id) => {
        if (window.confirm('Voulez-vous vraiment retirer ce produit du groupe ?')) {
            removeFromGroupMutation.mutate(id);
        }
    };

    // Bulk Mutations
    const bulkDeleteMutation = useMutation({
        mutationFn: async (ids) => {
            const res = await api.post('/scraped-products/bulk-delete', { ids });
            return res.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['scraped-products']);
            setSelectedIds(new Set());
            alert(data.message);
        },
        onError: (err) => alert('Bulk delete failed: ' + (err.response?.data?.error || err.message))
    });

    const bulkUpdateMutation = useMutation({
        mutationFn: async ({ ids, data }) => {
            const res = await api.post('/scraped-products/bulk-update', { ids, data });
            return res.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['scraped-products']);
            setSelectedIds(new Set());
            setShowBulkEditModal(false);
            alert(data.message);
        },
        onError: (err) => alert('Bulk update failed: ' + (err.response?.data?.error || err.message))
    });

    // Selection Handlers
    const toggleSelection = (id) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedIds(newSet);
    };

    const handleSelectAll = () => {
        if (selectedIds.size === filteredProducts.length && filteredProducts.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredProducts.map(p => p.id)));
        }
    };

    const handleBulkDelete = () => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.size} produits ?`)) {
            bulkDeleteMutation.mutate(Array.from(selectedIds));
        }
    };

    const handleBulkSave = (data) => {
        bulkUpdateMutation.mutate({
            ids: Array.from(selectedIds),
            data
        });
    };

    const handleDelete = (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ? ' + (showDeleted ? '(Suppression définitive ? NON IMPLÉMENTÉ, RESTAURATION SEULEMENT)' : '(Il sera déplacé dans la corbeille)'))) {
            deleteMutation.mutate(id);
        }
    };

    const handleRestore = (id) => {
        if (window.confirm('Voulez-vous restaurer ce produit ?')) {
            restoreMutation.mutate(id);
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <h1 className="text-3xl font-bold">Produits Scrappés</h1>

                {/* Bulk Action Bar - Sticky if filtered */}
                {selectedIds.size > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white shadow-xl border border-amber-200 rounded-full px-6 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-5 fade-in duration-300">
                        <span className="font-bold text-amber-900">{selectedIds.size} sélectionné(s)</span>
                        <div className="h-6 w-px bg-gray-200"></div>
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={handleBulkDelete}
                        >
                            Supprimer
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-200 text-amber-800 hover:bg-amber-50"
                            onClick={() => setShowBulkEditModal(true)}
                        >
                            Modifier...
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedIds(new Set())}
                        >
                            Annuler
                        </Button>
                    </div>
                )}

                {/* Top Action Bar */}
                <div className="flex items-center gap-4">
                    {/* ... existing buttons ... */}
                    <button
                        onClick={() => verifyGroupsMutation.mutate()}
                        disabled={verifyGroupsMutation.isPending}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                    >
                        {verifyGroupsMutation.isPending ? (<span className="animate-spin">🔄</span>) : (<span>🤖</span>)}
                        {verifyGroupsMutation.isPending ? 'Partie...' : 'Vérifier'}
                    </button>

                    {enrichmentStatus?.isRunning ? (
                        <div className="flex items-center gap-3 bg-amber-50 px-4 py-2 rounded-lg border border-amber-200 shadow-sm animate-pulse">
                            <span className="text-sm font-bold text-amber-800">En cours... ({enrichmentStatus.current}/{enrichmentStatus.total})</span>
                            <button onClick={() => stopEnrichmentMutation.mutate()} className="text-red-700 font-bold text-xs">Stop</button>
                        </div>
                    ) : (
                        <button onClick={() => enrichMutation.mutate()} className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 shadow-sm">
                            <span>✨</span>Enrichir
                        </button>
                    )}

                    {/* CLEANING CONTROL */}
                    {cleaningStatus?.isRunning ? (
                        <div className="flex items-center gap-3 bg-green-50 px-4 py-2 rounded-lg border border-green-200 shadow-sm animate-pulse">
                            <span className="text-sm font-bold text-green-700">Nettoyage... ({cleaningStatus.current}/{cleaningStatus.total})</span>
                            <button onClick={() => stopCleaningMutation.mutate()} className="text-red-700 font-bold text-xs">Stop</button>
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                if (window.confirm('Lancer le nettoyage des descriptions IA ? (Garde vocabulaire, supprime livraison/codes)')) {
                                    startCleaningMutation.mutate();
                                }
                            }}
                            disabled={startCleaningMutation?.isPending}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                        >
                            <span>🧹</span>Nettoyer Desc.
                        </button>
                    )}

                    <div className="flex items-center space-x-2 bg-white p-2 rounded-md border">
                        <input type="checkbox" id="deleted-mode" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} className="h-4 w-4" />
                        <label htmlFor="deleted-mode" className="text-sm font-medium text-red-700 cursor-pointer">🗑️ Corbeille</label>
                    </div>

                    <div className="flex items-center space-x-2 bg-white p-2 rounded-md border">
                        <input type="checkbox" id="variants-mode" checked={showVariantsOnly} onChange={(e) => setShowVariantsOnly(e.target.checked)} className="h-4 w-4" />
                        <label htmlFor="variants-mode" className="text-sm font-medium cursor-pointer">Variantes</label>
                    </div>

                    <div className="flex gap-2 bg-white p-1 rounded-md border">
                        <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded text-sm ${viewMode === 'table' ? 'bg-amber-100 text-amber-800' : ''}`}>📋</button>
                        <button onClick={() => setViewMode('cards')} className={`px-3 py-1 rounded text-sm ${viewMode === 'cards' ? 'bg-amber-100 text-amber-800' : ''}`}>🎴</button>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap gap-4 bg-gray-50 p-4 rounded-lg border">
                <Input
                    placeholder="Recherche..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full md:w-64"
                />

                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        placeholder="Min €"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className="w-24"
                    />
                    <span>-</span>
                    <Input
                        type="number"
                        placeholder="Max €"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className="w-24"
                    />
                </div>

                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="border rounded-md px-3 py-2 bg-white"
                >
                    <option value="all">Toutes les catégories</option>
                    {uniqueCategories.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <select
                    value={selectedColor}
                    onChange={(e) => {
                        setSelectedColor(e.target.value);
                        if (e.target.value !== 'all') setShowNoColor(false);
                    }}
                    className="border rounded-md px-3 py-2 bg-white"
                >
                    <option value="all">Toutes couleurs</option>
                    {uniqueColors.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                <div className="flex items-center space-x-2 bg-white p-2 rounded-md border">
                    <input
                        type="checkbox"
                        id="no-color-mode"
                        checked={showNoColor}
                        onChange={(e) => {
                            setShowNoColor(e.target.checked);
                            if (e.target.checked) setSelectedColor('all');
                        }}
                        className="h-4 w-4"
                    />
                    <label htmlFor="no-color-mode" className="text-sm font-medium cursor-pointer">Sans couleur</label>
                </div>

                <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    className="border rounded-md px-3 py-2 bg-white"
                >
                    <option value="all">Toutes tailles</option>
                    {uniqueSizes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <select
                    value={selectedSite}
                    onChange={(e) => {
                        setSelectedSite(e.target.value);
                    }}
                    className="border rounded-md px-3 py-2 bg-white"
                >
                    <option value="all">Tous sites</option>
                    {sitesData?.sites?.map((site) => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                </select>

                <Button
                    variant="outline"
                    onClick={() => {
                        setSearch('');
                        setMinPrice('');
                        setMaxPrice('');
                        setSelectedColor('all');
                        setShowNoColor(false);
                        setSelectedSize('all');
                        setSelectedCategory('all');
                        setSelectedSite('all');
                    }}
                >
                    Reset
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Total produits</CardDescription>
                        <CardTitle>{productsData?.stats?.total || 0}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Avec images</CardDescription>
                        <CardTitle>
                            {productsData?.stats?.withImages || 0}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Avec tailles</CardDescription>
                        <CardTitle>
                            {productsData?.stats?.withSizes || 0}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Avec couleurs</CardDescription>
                        <CardTitle>
                            {productsData?.stats?.withColors || 0}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Enrichis par IA</CardDescription>
                        <CardTitle>
                            {productsData?.stats?.enriched || 0}
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>

            {
                isLoading ? (
                    <p className="text-center py-8">Chargement...</p>
                ) : filteredProducts.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">
                        Aucun produit trouvé
                    </p>
                ) : viewMode === 'table' ? (
                    <div className="border rounded-md overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="p-4 w-10">
                                        <input
                                            type="checkbox"
                                            checked={filteredProducts.length > 0 && selectedIds.size === filteredProducts.length}
                                            onChange={handleSelectAll}
                                            className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                        />
                                    </th>
                                    <th className="p-4 font-medium w-20">Image</th>
                                    <th className="p-4 font-medium">Nom</th>
                                    <th className="p-4 font-medium">Prix</th>
                                    <th className="p-4 font-medium">Couleurs</th>
                                    <th className="p-4 font-medium">Groupe ID</th>
                                    <th className="p-4 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredProducts.map((product) => (
                                    <tr
                                        key={product.id}
                                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedIds.has(product.id) ? 'bg-amber-50' : ''}`}
                                        onClick={() => toggleSelection(product.id)}
                                    >
                                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(product.id)}
                                                onChange={() => toggleSelection(product.id)}
                                                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                            />
                                        </td>
                                        <td className="p-4">
                                            <div className="w-12 h-12 rounded overflow-hidden border bg-white" style={{ width: '50px', height: '50px', minWidth: '50px' }}>
                                                {product.images && product.images[0] ? (
                                                    <img
                                                        src={product.images[0]}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                                                        No
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4 font-medium max-w-xs truncate" title={product.name}>{product.name}</td>
                                        <td className="p-4 whitespace-nowrap">
                                            {product.price} €
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-wrap gap-1">
                                                {product.colors?.map((c, i) => (
                                                    <Badge key={i} variant="outline" className="text-xs">{getColorName(c)}</Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="p-4 text-xs font-mono text-gray-500 whitespace-nowrap">
                                            {product.variant_group_id ? (
                                                <div className="flex items-center gap-2">
                                                    <span title={product.variant_group_id} className="bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold">
                                                        #{product.variant_group_id.replace('verified_', '').substring(0, 6)}
                                                    </span>
                                                    <button
                                                        onClick={() => handleRemoveFromGroup(product.id)}
                                                        className="text-gray-400 hover:text-red-500"
                                                        title="Retirer du groupe"
                                                    >
                                                        ❌
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                <a
                                                    href={product.external_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-amber-600 hover:underline whitespace-nowrap"
                                                >
                                                    Voir
                                                </a>
                                                <button onClick={() => handleEdit(product)} className="text-gray-600 hover:text-gray-900 dark:text-white">
                                                    ✏️
                                                </button>
                                                {showDeleted ? (
                                                    <button onClick={() => handleRestore(product.id)} className="text-green-600 hover:text-green-900" title="Restaurer">
                                                        ♻️
                                                    </button>
                                                ) : (
                                                    <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-900">
                                                        🗑️
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                allProducts={productsData?.products || []}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onRestore={handleRestore}
                                isDeletedView={showDeleted}
                                isSelected={selectedIds.has(product.id)}
                                onToggleSelect={toggleSelection}
                            />
                        ))}
                    </div>
                )
            }
            {
                editingProduct && (
                    <EditProductModal
                        product={editingProduct}
                        isOpen={!!editingProduct}
                        onClose={() => setEditingProduct(null)}
                        onSave={(data) => updateMutation.mutate(data)}
                    />
                )
            }
            <BulkEditModal
                isOpen={showBulkEditModal}
                onClose={() => setShowBulkEditModal(false)}
                onSave={handleBulkSave}
                count={selectedIds.size}
            />

            <div className="mt-6 border-t pt-4">
                <Pagination
                    currentPage={page}
                    totalPages={productsData?.totalCount ? Math.ceil(productsData.totalCount / limit) : 1}
                    onPageChange={(newPage) => setPage(newPage)}
                />
                {productsData?.totalCount > 0 && (
                    <div className="text-center text-xs text-gray-500 mt-2">
                        Total: {productsData.totalCount} produits (Affichage {limit} par page)
                    </div>
                )}
            </div>
        </div >
    );
}
